// Number, money and date formatting in one place (en-US, the product's only locale for now).

const LOCALE = 'en-US';

export function formatPodHours(value: number, fractionDigits = 1): string {
  return value.toLocaleString(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatCount(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
}

/** `$399.00`, or `$399` with `whole: true` for plan prices. */
export function formatMoney(cents: number, currency = 'USD', whole = false): string {
  const digits = whole && cents % 100 === 0 ? 0 : 2;
  return (cents / 100).toLocaleString(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

const toDate = (value: Date | string): Date => (value instanceof Date ? value : new Date(value));

/** Sunday, October 25 */
export const formatWeekdayDate = (value: Date | string): string =>
  toDate(value).toLocaleDateString(LOCALE, { weekday: 'long', month: 'long', day: 'numeric' });

/** November 1 */
export const formatMonthDay = (value: Date | string): string =>
  toDate(value).toLocaleDateString(LOCALE, { month: 'long', day: 'numeric' });

/** Oct 1, 2026 */
export const formatShortDate = (value: Date | string): string =>
  toDate(value).toLocaleDateString(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });

/** 16:24 */
export const formatClock = (value: Date | string): string =>
  toDate(value).toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

/** just now, 2 min ago, 3 h ago, 4 days ago */
export function formatAgo(value: Date | string, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - toDate(value).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

/** 0:24 for a countdown in seconds. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

/** Whole days from now until the date, never negative. */
export function daysUntil(value: Date | string, now: Date = new Date()): number {
  return Math.max(0, Math.ceil((toDate(value).getTime() - now.getTime()) / 86_400_000));
}
